import React, { ReactElement, useEffect, useState } from 'react';
import { HeadTag } from 'shared/components';
import { Box } from '@mui/material';
import PolicyDataset from './components/PolicyDataset';
import PolicyList from './components/PolicyList';
import PageHeader from 'shared/components/PageHeader';
import { PoliciesService } from 'services';
import { IComplianceTemplate } from './policy-detail/components/Compliance';
import { useDispatch, useSelector } from 'react-redux';
import { IAppState } from 'state/RootReducer';
import { PaginationActions } from 'state/pagination/Action';

export interface IPolicy {
  _id: string;
  title: string;
  description: string;
  content: string;
  version: string;
  owner: string;
  status: string;
  effectiveDate: string;
  publishedDate: any;
  approvedDate: any;
  nextReviewDate: any;
  updatedAt: any;
  acknowledgementRequired: boolean;
  policyMode: string;
  policyUrl: string;
  reviewDate: string;
  lastReviewedDate: any;
  reviewIntervalMonths: number;
  needsReview: boolean;
  isActive: boolean;
  deletedAt: any;
  deletedBy: any;
  deletionReason: any;
  createdAt: string;
  coverage: string;
  __v: number;
  frameworkIds: IComplianceTemplate[];
}

export interface IPolicySummary {
  totalPolicies: number;
  totalAcknowledgements: number;
  completedAcknowledgements: number;
  // pendingAcknowledgements: number;
  acknowledgementPercentage: number;
  assigedAcknowledgements: number;
}

const PolicyManagement = (): ReactElement => {
  const dispatch = useDispatch();
  const policyPagination = useSelector(
    (state: IAppState) => state.paginationState.policy
  );
  const [isLoading, setIsLoading] = useState(false);
  const [policyList, setPolicyList] = useState<IPolicy[]>([]);
  const [summary, setSummary] = useState({
    totalPolicies: 0,
    totalAcknowledgements: 0,
    completedAcknowledgements: 0,
    // pendingAcknowledgements: 0,
    acknowledgementPercentage: 0,
    assigedAcknowledgements: 0,
  });

  // API Calls
  const loadRecords = (): void => {
    setIsLoading(true);
    let tabParam = 'all';
    if (policyPagination.tab === 'Pending Review') tabParam = 'review';
    else if (policyPagination.tab === 'Published') tabParam = 'published';
    else if (policyPagination.tab === 'Draft') tabParam = 'draft';
    else if (policyPagination.tab === 'Archived') tabParam = 'archived';
    else if (policyPagination.tab === 'All policies') tabParam = 'all';

    const queryString = new URLSearchParams({
      offset: String(policyPagination.offset),
      limit: String(policyPagination.pageLimit),
      title: policyPagination.search,
      tab: tabParam,
    }).toString();
    PoliciesService.getAll(queryString)
      .then((response) => {
        const { data } = response.data;
        const { policies, pagination } = data;
        setPolicyList(policies);
        setSummary({ ...data });

        dispatch(
          PaginationActions.setPagination({
            policy: {
              offset: pagination.offset,
              pageLimit: pagination.limit,
              totalRecords: pagination.total,
              totalPages: pagination.totalPages,
              search: policyPagination.search || '',
              tab: policyPagination.tab || 'all',
            },
          })
        );
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => setIsLoading(false));
  };

  // Hooks
  useEffect(() => {
    loadRecords();
  }, [
    policyPagination.offset,
    policyPagination.pageLimit,
    policyPagination.search,
    policyPagination.tab,
  ]);

  return (
    <>
      <PageHeader
        title="Policy Management"
        description="Manage, distribute, and track organisation-wide policies
"
      />
      <Box className="policy-management policy-management manage">
        <HeadTag title="Policy Management" />

        <Box className="policy-management-wrapper">
          <PolicyDataset
            totalPolicies={summary.totalPolicies}
            acknowledgementPercentage={summary.acknowledgementPercentage}
            totalAcknowledgements={summary.totalAcknowledgements}
            completedAcknowledgements={summary.completedAcknowledgements}
            assigedAcknowledgements={summary.assigedAcknowledgements}
          />

          <PolicyList policyList={policyList} isLoading={isLoading} />
        </Box>
      </Box>
    </>
  );
};

export default PolicyManagement;
